/**
 * The delivery bar at the foot of the run's phase card — Figma `500:1295` ("SyncStatus — CANONICAL
 * SPEC"), whose in-situ frame is `544:842` ("Delivery"), drawn under the `rule` at `500:1069`.
 *
 * ── WHY THIS IS NOT `LiveRunProgress` ───────────────────────────────────────
 * `LiveRunProgress` (deleted in `5b1044a2`) rendered UNCONDITIONALLY on every live run, filled
 * continuously from a metric that reads 0 for most of a run's life, and moved between readings. This
 * component is the opposite on all three counts, and each one is a gate rather than a style choice:
 *
 *   CONDITIONAL — the bar renders only where the underlying number is groundable. Figma annotation
 *     `281:289`, verbatim: "Groundable only when changed.total > 10,000 AND the run outlives one ~30s
 *     reporting tick. Below one batch the count reads 0 or everything however long it runs — so most
 *     real runs never qualify, and the no-bar case is the default, not the exception. Discrete
 *     segments, labelled up to 30s stale, never animated. changed.total === −1 is a sentinel: no bar."
 *   DISCRETE — the fill is whatever the last flush said, painted where it is. There is no
 *     interpolation between two readings and no easing into a new width.
 *   NEVER ANIMATED — no `transition-*`, no `animate-*`, no `duration-*` anywhere in this file.
 *     `prefers-reduced-motion` is moot: there is no motion to reduce. `DeliveryProgress.test.tsx`
 *     pins the absence of those class prefixes on the whole subtree, because "we just won't add one"
 *     is not a constraint.
 *
 * ── WHERE THE TWO THRESHOLDS COME FROM ──────────────────────────────────────
 * Both are platform constants, and `run-fixtures.ts` carries the measurements:
 *
 *   {@link BATCH_SIZE_RECORDS} is `gateway.maxBatchSizeEvents`. A plan at or below one batch moves
 *     from 0 to everything in ONE step — `liveSendingNothingDelivered` (1,230 changed, 0 accepted at
 *     +0.2s) and `liveSendingTotalWithoutBreakdown` (the same run, 1,230 accepted at +19.3s) are the
 *     two observed samples, and there is nothing in between them at any duration. A bar over that is
 *     a two-position light drawn as a continuum.
 *   {@link REPORTING_TICK_SECONDS} is `reporting.asyncReporterInterval`. Counts flush on that
 *     interval, so a run gets `duration ÷ 30s` readings and its FIRST reading is 0 whatever its size.
 *     `liveSendingMidFlight` is the 400,000-row run that clears both gates: seven intermediate
 *     flushes at 31.6 / 29.6 / 30.1 / 29.8 / 30.6 / 29.5 / 30.5 second gaps.
 *
 * A large run inside its first tick therefore renders NOTHING, not an empty track: an empty track is
 * a reading of 0%, which is exactly the "false 0% for the first sixth of the run" defect the deleted
 * component shipped. It is also not given the single-batch caption, which would be a plain lie about
 * a 400,000-row plan. The slot is simply not there yet.
 *
 * ── THE SUB-BATCH SLOT IS NOT AN EMPTY STATE ────────────────────────────────
 * Figma draws it as its own variant (`State=Sending · single batch`, `500:1291`): the same head row
 * with `sent in one batch` where the in-flight count would be, no track at all, and a caption that
 * says why a 0 is expected here rather than a stall. That is the majority case for real audiences, so
 * it gets an explanation rather than a blank.
 *
 * ── AGREEMENT WITH THE SEND ROW ─────────────────────────────────────────────
 * The fill is `successful.total / changed.total`, which is the SAME pair `deriveRunPhases` hands the
 * Send row (`run-phases.ts`), and the accessible value text is built by `denominated()` — the one
 * formatter that row also goes through. The bar and the row cannot disagree because they are not two
 * derivations of one number; they are one derivation rendered twice.
 *
 * ── KNOWN, UNRESOLVED: THE IN-FLIGHT COUNT APPEARS TWICE ────────────────────
 * The head's right-hand slot is `unaccounted(run)` — the same number `RunShortfall` renders below the
 * card as `237,620 records in flight`. Figma's two live states draw no shortfall line, which reads as
 * an intent that this head REPLACES it while the bar is up; but that frame does not model
 * `RunShortfall` in any live state, so its absence is as easily an omission as a decision. Suppressing
 * a separately-designed, separately-tested component on the strength of an absence is exactly the
 * confident-but-wrong move this codebase keeps paying for, so nothing here touches `RunShortfall` and
 * the duplication stands, flagged, until a designer settles it. What is NOT in doubt: both numbers
 * come from `unaccounted()`, so they can be redundant but never contradictory.
 */
import { cn } from '@/_shared/lib/utils';

import { denominated, exact, isMeasured, isRunning, runDurationSeconds, unaccounted } from '../lib/run-view';
import { useTickingNow } from '../lib/use-ticking-now';
import type { RawSyncRun } from '../types';

/**
 * `gateway.maxBatchSizeEvents`. A plan at or below this reads 0 or everything and never anything
 * between, so it is the floor for a fraction being groundable at all. Figma `281:289` writes the gate
 * as `> 10,000`, strictly — a plan of exactly one batch is still one step.
 */
export const BATCH_SIZE_RECORDS = 10_000;

/**
 * `reporting.asyncReporterInterval`. The run must outlive one of these for a reading to exist that is
 * not the initial 0.
 */
export const REPORTING_TICK_SECONDS = 30;

const HEAD_LABEL = 'ACCEPTED';

/** Verbatim from Figma `544:849`. The staleness bound is the reporting interval, stated in words. */
const BAR_CAPTION =
  'The accepted count updates about every 30 seconds, in large steps — so it can be up to 30s behind.';

/**
 * Figma `500:1132` / `500:1133` word this "counted in one step". Deliberately NOT verbatim here
 * (owner call, 2026-09-02): "step" is a word this product uses nowhere else, and the thing being
 * described is a batch — the send splits on `gateway.maxBatchSizeEvents`, and a run under that limit
 * flushes once. Naming the real mechanism is both plainer and more accurate than inventing a
 * synonym for it. If the frame is ever re-exported, this is the intended divergence, not drift.
 */
const SINGLE_BATCH_LABEL = 'sent in one batch';
const SINGLE_BATCH_CAPTION =
  'This run fits in one batch, so the count stays at 0 until every record has landed. Expected at this size — not a stall.';

/**
 * The rule above the block belongs to the block, not to the caller: it is drawn only when there is
 * something under it, and a caller that had to know whether this component returns null in order to
 * decide whether to draw a separator would be re-deriving the whole gate.
 */
const ROOT =
  'mt-2.5 flex w-[22rem] flex-col gap-1.5 border-t border-border pt-2.5 text-[0.6875rem] leading-[0.8125rem]';

function Frame({ trailing, caption, children, className }: FrameProps): React.ReactElement {
  return (
    <div data-slot="delivery-progress" className={cn(ROOT, className)}>
      <div className="flex items-center justify-between gap-2">
        {/* `tracking-[0.025rem]` is the spec's 0.4px, in rem per ui/AGENTS.md's no-px rule. */}
        <span className="font-semibold tracking-[0.025rem] text-foreground">{HEAD_LABEL}</span>
        <span className="text-muted-foreground">{trailing}</span>
      </div>
      {children}
      <p className="text-muted-foreground">{caption}</p>
    </div>
  );
}

interface FrameProps {
  /** The head's right-hand slot: an in-flight count, or the words that stand in for one. */
  trailing: string;
  caption: string;
  /** The track, when there is a groundable fraction to draw. Absent is the common case. */
  children?: React.ReactNode;
  className?: string | undefined;
}

export function DeliveryProgress({
  run,
  className,
}: {
  run: RawSyncRun;
  /** Adjusts the frame's layout for a different host (`RunStrip`'s full width); the gate is untouched. */
  className?: string;
}): React.ReactElement | null {
  // Ticks only while the run is live, and the gate below needs elapsed time — same hook, same reason,
  // as `RunIdentityCard`. Called before every early return: it is a hook, not a branch.
  const now = useTickingNow(isRunning(run));

  // A settled run states its outcome; it has no remaining work for a bar to be about. Figma's
  // `State=Settled` / `State=Failed in send` / `State=Failed in extract` all draw the rule and the
  // block away entirely rather than freezing the bar at its last reading.
  if (!isRunning(run)) return null;

  const changed = run.metrics?.changed?.total ?? 0;

  // `changed.total === -1` is the count-query-timeout sentinel: not a quantity, so not a denominator.
  // `changed === 0` is the extract half, where nothing has been planned yet (`liveExtract`, where every
  // metric on the wire is a placeholder 0) — Figma's `State=Extracting` draws no block at all.
  if (!isMeasured(changed) || changed === 0) return null;

  const accepted = run.metrics?.successful?.total ?? 0;

  // One batch or less: two positions, however long the run takes. Explained, not drawn.
  if (changed <= BATCH_SIZE_RECORDS) {
    return <Frame className={className} trailing={SINGLE_BATCH_LABEL} caption={SINGLE_BATCH_CAPTION} />;
  }

  // Large, but the first flush has not landed — every reading so far is the initial 0.
  if (runDurationSeconds(run, now) <= REPORTING_TICK_SECONDS) return null;

  // `unaccounted()` = `changed − (successful + failed + dropped)`, and its own doc requires a caller
  // that RENDERS it to refuse a negative: "there is no honest sentence containing '-100 records
  // unaccounted for'". A payload that contradicts itself gets the default treatment — no bar — rather
  // than a clamped one that hides the contradiction.
  const inFlight = unaccounted(run);
  if (inFlight === null || inFlight < 0) return null;

  // THE READING is `denominated()` — the same call the Send row makes, so the two are one derivation
  // rendered twice rather than two derivations that happen to agree today. `percent()` inside it does
  // NOT clamp, and is left that way on purpose: a fraction over 100% is a payload contradicting
  // itself, and flattening it to "100.0%" would erase the only evidence that it did — the same mistake
  // as formatting the `-1` sentinel as a quantity.
  //
  // THE WIDTH is that fraction, unclamped too, and there is no `Math.min` here on purpose either.
  // Two things already bound it, so a clamp would be unreachable code wearing a confident comment:
  // the `inFlight >= 0` guard above means `successful + failed + dropped <= changed`, hence
  // `accepted <= changed`; and the track carries `overflow-hidden` (Figma's own `overflow-clip`), so
  // even an arithmetic that got past that guard clips at the track's edge instead of painting outside
  // it. The bound is geometric, where a bound belongs — never applied to the number.
  const reading = denominated(accepted, changed, 'changed');
  const width = (accepted / changed) * 100;

  return (
    <Frame className={className} trailing={`${exact(inFlight)} still in flight`} caption={BAR_CAPTION}>
      {/* `aria-valuemin`/`max` are the RECORD counts, not 0–100: the numbers a reader is given in
          text are the numbers the widget reports, so `aria-valuenow` needs no separate percentage to
          be meaningful. `aria-valuetext` carries the whole sentence because "162380" announced against
          a max of "400000" is not what the row says out loud. */}
      <div
        role="progressbar"
        aria-label="Records accepted by the destination"
        aria-valuemin={0}
        aria-valuemax={changed}
        aria-valuenow={accepted}
        aria-valuetext={`${reading.value} accepted of ${reading.of ?? ''} changed · ${reading.percent ?? ''}`}
        className="h-1.5 w-full overflow-hidden rounded-[0.125rem] bg-muted"
      >
        {/* NO transition, NO animation. The width is set from the last flush and left there. The
            figure comes from the DATA: Figma's rect is 149 of 344 (43.3%) while its own layer name
            says "fill · 40.6% accepted", and 162,380/400,000 IS 40.6% — the rect is an imprecise
            drawing of the label, so the label wins and neither number is hardcoded. */}
        <div
          data-slot="delivery-fill"
          className="h-full rounded-[0.125rem] bg-success"
          style={{ width: `${width.toFixed(1)}%` }}
        />
      </div>
    </Frame>
  );
}
